import { DestroyRef, inject, Injectable } from "@angular/core";
import { BehaviorSubject, distinctUntilChanged, first, map, merge, Observable } from "rxjs";
import { InterpolationParameters, LangChangeEvent, TranslateService, Translation } from "@ngx-translate/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CommonService } from "../common";
import { APP_LANGUAGE_TOKEN } from "./default-language-token";
import { LanguageDto } from "../../dto";

/**
 * To be sure that this service is always created and will enable translations there are two ways:
 *  1. Preferred way:
 *  Add this to app providers:
 *  <code>
 *     {
 *       provide: APP_INITIALIZER,
 *       useFactory: initializeGastTranslationService,
 *       deps: [TranslationService],
 *       multi: true,
 *     },
 *  </code>
 *  2. Second way:
 *  Inject the service to any component to be sure that the service will not be tree shaken.
 */
@Injectable({
  providedIn: "root",
})
export class TranslationService {
  readonly #defaultLanguage = "de";
  readonly #commonService = inject(CommonService);
  readonly #ngxTranslateService = inject(TranslateService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #appLanguage = inject<string | undefined>(APP_LANGUAGE_TOKEN);
  readonly #appDefaultLanguage = this.#appLanguage || this.#defaultLanguage;
  readonly #initLanguageUsed$$ = new BehaviorSubject<string>(this.#appDefaultLanguage);

  /**
   * Returns the changed language code but only for changes after initialization.
   */
  readonly currentLanguageChanged$: Observable<string> = this.#ngxTranslateService.onLangChange.pipe(
    takeUntilDestroyed(this.#destroyRef),
    map((changeEvent: LangChangeEvent) => changeEvent.lang.toLowerCase()),
    distinctUntilChanged()
  );

  /**
   * Returns the init language code and all further changed languages.
   */
  readonly initOrLanguageChanged$: Observable<string> = merge(
    this.#initLanguageUsed$$.asObservable(),
    this.currentLanguageChanged$
  ).pipe(
    distinctUntilChanged(),
    map((lang: string): string => lang.toLowerCase()),
    takeUntilDestroyed(this.#destroyRef)
  );

  constructor() {
    this.#ngxTranslateService.setDefaultLang(this.#appDefaultLanguage);
    this.#ngxTranslateService.use(this.#appDefaultLanguage);
    this.#commonService.languagesLoaded
      .pipe(takeUntilDestroyed(this.#destroyRef), distinctUntilChanged())
      .subscribe((languages: LanguageDto[]) => {
        this.#ngxTranslateService.addLangs(languages.map((l: LanguageDto) => l.code.toLowerCase()));
      });
  }

  changeLanguage(language: LanguageDto): void {
    this.#ngxTranslateService.use(language.code.toLowerCase());
    this.#ngxTranslateService.currentLang = language.code.toLowerCase();
  }

  changeLanguageUsingCode(languageCode: string): void {
    this.#ngxTranslateService.use(languageCode.toLowerCase());
    this.#ngxTranslateService.currentLang = languageCode.toLowerCase();
  }

  getCurrentLanguageCode(): string {
    return this.#ngxTranslateService.currentLang.toLowerCase();
  }

  getDefaultLanguage(): string {
    return this.#appDefaultLanguage;
  }

  /**
   * Returns a translation in currently set language or undefined if the translation was not found.
   * @param key {@link string} translation key
   * @param interpolateParams optional translation params
   */
  translateKey(key: string, interpolateParams?: InterpolationParameters): string | undefined {
    const result = this.#ngxTranslateService.instant(key, interpolateParams);
    return result !== key ? result : undefined;
  }

  /**
   * Returns observable of translation in currently set language or of the default value if it was not found.
   * By default, the default value param is set to empty string.
   *
   * Note:
   * If the ngx translate service does not find the translation in its store, it will return the key back.
   * Hence, the predicate to check if the value was found.
   *
   * @param key
   * @param defaultValue
   */
  translateKeyDeferred(key: string, defaultValue = ""): Observable<Translation> {
    return this.#ngxTranslateService
      .get(key)
      .pipe(first<string, string>((foundTranslation) => foundTranslation !== key, defaultValue));
  }
}
